import { useCallback, useEffect, useRef, useState } from 'react';
import { getUsers, type UsersPage } from '../api/users.api';
import { mapUser, type User } from '../types/user.types';

const usersCache = new Map<string, { users: User[]; meta: UsersPage['meta'] }>();

function usersCacheKey(page: number, pageSize: number, search: string): string {
  return `${page}:${pageSize}:${search.trim()}`;
}

function formatUsersRequestError(requestError: unknown): string {
  const record = requestError && typeof requestError === 'object'
    ? requestError as Record<string, unknown>
    : {};
  const status = typeof record.status === 'number' ? `HTTP ${record.status}` : null;
  const body = record.body && typeof record.body === 'object' && !Array.isArray(record.body)
    ? record.body as Record<string, unknown>
    : null;
  const detail = body && typeof body.detail === 'string' ? body.detail : null;
  const context = [status, detail].filter(Boolean).join(' — ');

  return context
    ? `خطا در دریافت فهرست کاربران (${context})`
    : 'خطا در دریافت فهرست کاربران';
}

function logUsersRequestFailure(requestError: unknown, pageSize: number): void {
  const record = requestError && typeof requestError === 'object'
    ? requestError as Record<string, unknown>
    : {};
  const status = typeof record.status === 'number' ? record.status : undefined;
  const body = record.body && typeof record.body === 'object' && !Array.isArray(record.body)
    ? record.body as Record<string, unknown>
    : null;
  const detail = body && typeof body.detail === 'string' ? body.detail : undefined;

  console.error('[users request diagnostic]', {
    page_size: pageSize,
    status,
    detail,
  });
}

export function useUsers(page: number, pageSize = 15, search = '') {
  const cached = usersCache.get(usersCacheKey(page, pageSize, search));
  const [users, setUsers] = useState<User[]>(() => cached?.users ?? []);
  const [meta, setMeta] = useState<UsersPage['meta']>(() => cached?.meta ?? ({ page, limit: pageSize, total: 0 }));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const requestGenerationRef = useRef(0);

  const loadUsers = useCallback(() => {
    const generation = ++requestGenerationRef.current;
    const isCurrentRequest = () =>
      mountedRef.current && requestGenerationRef.current === generation;

    if (isCurrentRequest()) {
      setLoading(true);
      setError(null);
    }

    return getUsers(page, pageSize, search)
      .then((data) => {
        if (!isCurrentRequest()) return;
        const nextUsers = data.users.map(mapUser);
        usersCache.set(usersCacheKey(page, pageSize, search), { users: nextUsers, meta: data.meta });
        setUsers(nextUsers);
        setMeta(data.meta);
      })
      .catch((requestError) => {
        logUsersRequestFailure(requestError, pageSize);
        if (isCurrentRequest()) setError(formatUsersRequestError(requestError));
      })
      .finally(() => {
        if (isCurrentRequest()) setLoading(false);
      });
  }, [page, pageSize, search]);

  useEffect(() => {
    mountedRef.current = true;
    loadUsers();
    return () => { mountedRef.current = false; };
  }, [loadUsers]);

  return { users, meta, loading, error, refetch: loadUsers };
}
